import React, { useMemo, useCallback } from "react";
import { roundedPanelSx } from "../../overviewPanel";
import { Box, Typography, Skeleton, useTheme, useMediaQuery } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useCompanyPriceEod } from "../hooks/useCompanyPriceEod";
import { useAnalystPriceTargets } from "../hooks/useAnalystPriceTargets";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatStockPrice } from "../../../../utils/stockPriceFormatter";
import { Theme } from "@mui/material/styles";

dayjs.extend(utc);
dayjs.extend(timezone);

// Flat UI Color System - Theme Aware
const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    primary: "#22c55e",
    primaryHover: "#16a34a",
    negative: "#ef4444",
    background: isDark ? "#1a1a1a" : "#ffffff",
    surface: isDark ? "#212121" : "#f8fafc",
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
    borderSubtle: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
    textPrimary: isDark ? "#e4e6ea" : "#0f172a",
    textSecondary: isDark ? "#9ca3af" : "#64748b",
    textMuted: isDark ? "#6b7280" : "#94a3b8",
    accent: isDark ? "#60a5fa" : "#3b82f6",
  };
};

interface PriceTargetChartProps {
  ticker: string;
}

interface ChartDataPoint {
  x: string;
  y: number;
  originalDate: string;
  close: number;
}

export const PriceTargetChart: React.FC<PriceTargetChartProps> = ({
  ticker,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const flatUIColors = useMemo(() => getFlatUIColors(theme), [theme]);
  const { currency } = useStockCurrency();

  // Calculate date range for 1 year of historical data
  const dateRange = useMemo(() => {
    const to = dayjs().format("YYYY-MM-DD");
    const from = dayjs().subtract(1, "year").format("YYYY-MM-DD");
    return { from, to };
  }, []);

  // Fetch 1 year of historical price data (EOD for cleaner chart)
  const {
    data: priceData,
    isLoading: priceLoading,
    error: priceError,
  } = useCompanyPriceEod(ticker, dateRange.from, dateRange.to);

  // Fetch analyst price targets
  const {
    data: targetData,
    isLoading: targetLoading,
    error: targetError,
  } = useAnalystPriceTargets(ticker);

  const isLoading = priceLoading || targetLoading;
  const hasError = priceError || targetError;

  // Calculate projection end date (1 year from now)
  const projectionEndDate = useMemo(() => {
    return dayjs().add(1, "year").format("YYYY-MM-DD");
  }, []);

  // Process historical price data into chart format
  const { chartData, historicalPrices, lastDataPoint } = useMemo(() => {
    if (!priceData?.data || priceData.data.length === 0) {
      return { chartData: [], historicalPrices: [], lastDataPoint: null };
    }

    const processedData = priceData.data
      .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
      .map((item) => {
        const close =
          isFinite(item.close) && !isNaN(item.close) ? item.close : 0;
        // Normalize date to YYYY-MM-DD format for time scale
        const normalizedDate = dayjs(item.date).format("YYYY-MM-DD");
        return {
          x: normalizedDate,
          y: close,
          originalDate: item.date,
          close,
        };
      })
      .filter((item) => isFinite(item.y) && !isNaN(item.y));

    const prices = processedData.map((d) => d.close);
    const lastPoint = processedData[processedData.length - 1] || null;

    // Add synthetic future point to extend x-axis (null y so no line is drawn)
    const dataWithProjection = [
      ...processedData,
      {
        x: projectionEndDate,
        y: null as any, // null y-value means no line drawn to this point
        originalDate: projectionEndDate,
        close: null as any,
      },
    ];

    return {
      chartData: [{ id: "price", data: dataWithProjection }],
      historicalPrices: prices,
      lastDataPoint: lastPoint,
    };
  }, [priceData, projectionEndDate]);

  // Calculate Y-axis bounds including targets
  const { yMin, yMax } = useMemo(() => {
    if (historicalPrices.length === 0 || !targetData) {
      return { yMin: 0, yMax: 100 };
    }

    // Sparse coverage is normal down-cap: FMP rows can carry null targets, and
    // a null in the domain math coerces to 0 and floors the whole chart.
    const finiteTargets = [
      targetData.targetLow,
      targetData.targetMedian,
      targetData.targetHigh,
    ].filter((price): price is number => Number.isFinite(price));
    const allPrices = [...historicalPrices, ...finiteTargets];

    const min = Math.min(...allPrices);
    const max = Math.max(...allPrices);
    const range = max - min;

    return {
      yMin: min - range * 0.05,
      yMax: max + range * 0.05,
    };
  }, [historicalPrices, targetData]);

  // Custom projection layer
  const projectionLayer = useCallback(
    ({ xScale, yScale }: any) => {
      if (!lastDataPoint || !targetData) return null;

      // Per-field guards: a null target coerces to 0 through yScale (phantom
      // $0.00 at the chart floor) and an undefined one goes NaN, which then
      // poisons the label de-conflict pass below. Draw only what's real.
      const hasLow = Number.isFinite(targetData.targetLow);
      const hasMedian = Number.isFinite(targetData.targetMedian);
      const hasHigh = Number.isFinite(targetData.targetHigh);
      if (!hasLow && !hasMedian && !hasHigh) return null;

      // Get the x position of the last historical data point
      // Time scale expects Date objects
      const lastX = xScale(new Date(lastDataPoint.x));

      // Projection extends to the projection end date (1 year from now)
      const projectionEndX = xScale(new Date(projectionEndDate));

      // Calculate Y positions
      const currentY = yScale(lastDataPoint.close);
      const lowY = yScale(targetData.targetLow);
      const medianY = yScale(targetData.targetMedian);
      const highY = yScale(targetData.targetHigh);

      // Label dimensions - responsive for mobile
      const labelWidth = isMobile ? 80 : 90;
      const labelHeight = isMobile ? 22 : 24;
      const labelFontSize = isMobile ? 11 : 12;

      // Colors for target lines
      const highColor = "#22c55e"; // Green for high
      const medianColor = "#f59e0b"; // Orange/amber for median
      const lowColor = "#ef4444"; // Red for low

      // De-conflict the price labels: targets that format to the SAME price
      // collapse into one label (median wins), and a label that would overprint
      // an already-kept one is DROPPED rather than nudged (2026-07-02) — a
      // nudged label no longer aligns with its true y and misreads against the
      // axis, and the header row above the chart always shows all three values.
      const labelCandidates: { key: string; y: number; text: string }[] = [];
      if (hasMedian) {
        labelCandidates.push({
          key: "median",
          y: medianY,
          text: formatStockPrice({ price: targetData.targetMedian, currency }),
        });
      }
      if (hasHigh) {
        labelCandidates.push({
          key: "high",
          y: highY,
          text: formatStockPrice({ price: targetData.targetHigh, currency }),
        });
      }
      if (hasLow) {
        labelCandidates.push({
          key: "low",
          y: lowY,
          text: formatStockPrice({ price: targetData.targetLow, currency }),
        });
      }
      // First occurrence wins on identical text — median is listed first.
      const byText = new Map<string, (typeof labelCandidates)[number]>();
      labelCandidates.forEach((entry) => {
        if (!byText.has(entry.text)) byText.set(entry.text, { ...entry });
      });
      const sortedLabels = Array.from(byText.values()).sort((a, b) => a.y - b.y);
      const minLabelGap = labelHeight;
      // Keep top-to-bottom; drop any label within a label height of the last
      // kept one. Every rendered label stays at its true y.
      const targetLabels: typeof sortedLabels = [];
      sortedLabels.forEach((entry) => {
        const lastKept = targetLabels[targetLabels.length - 1];
        if (!lastKept || entry.y - lastKept.y >= minLabelGap) targetLabels.push(entry);
      });

      return (
        <g>
          {/* Dotted line to Low target */}
          {hasLow && (
            <line
              x1={lastX}
              y1={currentY}
              x2={projectionEndX}
              y2={lowY}
              stroke={lowColor}
              strokeWidth={1.5}
              strokeDasharray="5 6"
              strokeDashoffset={0}
              opacity={0.8}
            />
          )}

          {/* Dotted line to High target */}
          {hasHigh && (
            <line
              x1={lastX}
              y1={currentY}
              x2={projectionEndX}
              y2={highY}
              stroke={highColor}
              strokeWidth={1.5}
              strokeDasharray="5 6"
              strokeDashoffset={6}
              opacity={0.8}
            />
          )}

          {/* Dotted line to Median target */}
          {hasMedian && (
            <line
              x1={lastX}
              y1={currentY}
              x2={projectionEndX}
              y2={medianY}
              stroke={medianColor}
              strokeWidth={2}
              strokeDasharray="5 6"
              strokeDashoffset={3}
              opacity={0.8}
            />
          )}

          {/* Current price marker */}
          <circle
            cx={lastX}
            cy={currentY}
            r={5}
            fill="#22c55e"
            stroke={theme.palette.background.paper}
            strokeWidth={2}
          />

          {/* Target markers at projection end (small circles) */}
          {hasHigh && <circle cx={projectionEndX} cy={highY} r={3} fill={highColor} />}
          {hasMedian && <circle cx={projectionEndX} cy={medianY} r={4} fill={medianColor} />}
          {hasLow && <circle cx={projectionEndX} cy={lowY} r={3} fill={lowColor} />}

          {/* Target price labels — de-conflicted: duplicates collapsed, overlapping labels dropped */}
          {targetLabels.map((label) => (
            <foreignObject
              key={label.key}
              x={projectionEndX + 8}
              y={label.y - labelHeight / 2}
              width={labelWidth}
              height={labelHeight}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  height: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: labelFontSize,
                    fontWeight: 600,
                    color: flatUIColors.textPrimary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {label.text}
                </Typography>
              </Box>
            </foreignObject>
          ))}

        </g>
      );
    },
    [lastDataPoint, targetData, flatUIColors, currency, theme.palette.background.paper, projectionEndDate, isMobile]
  );

  // Chart layers
  const chartLayers = useMemo(
    () =>
      [
        "grid",
        "markers",
        "axes",
        "areas",
        "lines",
        "points",
        "slices",
        "legends",
        projectionLayer,
      ] as const,
    [projectionLayer]
  );

  // Loading state
  if (isLoading) {
    return (
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        {/* Summary skeleton */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: { xs: 2.5, md: 4 },
            mb: 3,
            pb: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ textAlign: { xs: "left", sm: "center" } }}>
              <Skeleton variant="text" width={80} height={14} sx={{ mb: 0.5, mx: { xs: 0, sm: "auto" } }} />
              <Skeleton variant="text" width={100} height={32} sx={{ mx: { xs: 0, sm: "auto" } }} />
              <Skeleton variant="text" width={50} height={16} sx={{ mt: 0.5, mx: { xs: 0, sm: "auto" } }} />
            </Box>
          ))}
        </Box>
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Error or no data state
  if (hasError || !priceData?.data?.length || !targetData) {
    return (
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        <Typography
          sx={{
            color: theme.palette.text.primary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.875rem",
            fontWeight: 600,
            mb: 2,
          }}
        >
          Analyst Price Targets
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          Price target data not available for this stock
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
      {/* Price Target Summary */}
      {(() => {
        const currentPrice = targetData.currentPrice;
        // Sparse FMP rows: a missing target shows "—" with no upside line —
        // never a phantom $0.00 or a -100% derived from a null.
        const formatTarget = (price: number): string =>
          Number.isFinite(price) ? formatStockPrice({ price, currency }) : "—";
        const upsideOf = (target: number): number | null =>
          Number.isFinite(target) && Number.isFinite(currentPrice) && currentPrice > 0
            ? ((target - currentPrice) / currentPrice) * 100
            : null;
        const lowUpside = upsideOf(targetData.targetLow);
        const medianUpside = upsideOf(targetData.targetMedian);
        const highUpside = upsideOf(targetData.targetHigh);

        return (
          <Box
            sx={{
              display: "grid",
              // Always one row of three — on phones the type scales down
              // rather than the blocks stacking into a tall column.
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: { xs: 1.5, sm: 2.5, md: 4 },
              mb: 3,
              pb: 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Low Target */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: { xs: "0.03em", sm: "0.05em" },
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  mb: 0.5,
                }}
              >
                Low Target
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.05rem", sm: "1.5rem", md: "1.75rem" },
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  lineHeight: 1.2,
                }}
              >
                {formatTarget(targetData.targetLow)}
              </Typography>
              {lowUpside !== null && (
                <Typography
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    fontWeight: 600,
                    color: lowUpside >= 0 ? "#22c55e" : "#ef4444",
                    mt: 0.5,
                  }}
                >
                  {lowUpside >= 0 ? "+" : ""}{lowUpside.toFixed(1)}%
                </Typography>
              )}
            </Box>

            {/* Median Target - Emphasized */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: { xs: "0.03em", sm: "0.05em" },
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  mb: 0.5,
                }}
              >
                Median Target
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", sm: "1.75rem", md: "2rem" },
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  lineHeight: 1.2,
                }}
              >
                {formatTarget(targetData.targetMedian)}
              </Typography>
              {medianUpside !== null && (
                <Typography
                  sx={{
                    fontSize: { xs: "0.72rem", sm: "0.875rem" },
                    fontWeight: 600,
                    color: medianUpside >= 0 ? "#22c55e" : "#ef4444",
                    mt: 0.5,
                  }}
                >
                  {medianUpside >= 0 ? "+" : ""}{medianUpside.toFixed(1)}%
                </Typography>
              )}
            </Box>

            {/* High Target */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: { xs: "0.03em", sm: "0.05em" },
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  mb: 0.5,
                }}
              >
                High Target
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.05rem", sm: "1.5rem", md: "1.75rem" },
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  lineHeight: 1.2,
                }}
              >
                {formatTarget(targetData.targetHigh)}
              </Typography>
              {highUpside !== null && (
                <Typography
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    fontWeight: 600,
                    color: highUpside >= 0 ? "#22c55e" : "#ef4444",
                    mt: 0.5,
                  }}
                >
                  {highUpside >= 0 ? "+" : ""}{highUpside.toFixed(1)}%
                </Typography>
              )}
            </Box>
          </Box>
        );
      })()}

      {/* Chart */}
      <Box sx={{ height: 280 }}>
        <ResponsiveLine
          data={chartData}
          animate={false}
          margin={{
            top: isMobile ? 10 : 20,
            right: isMobile ? 60 : 100, // Extra space for target labels
            bottom: isMobile ? 50 : 40,
            left: isMobile ? 70 : 80, // Extra space for price labels
          }}
          xScale={{
            type: "time",
            format: "%Y-%m-%d",
            useUTC: false,
            precision: "day",
          }}
          yScale={{
            type: "linear",
            min: yMin,
            max: yMax,
            stacked: false,
            reverse: false,
          }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          xFormat="time:%Y-%m-%d"
          axisBottom={{
            tickSize: 0,
            tickPadding: isMobile ? 8 : 12,
            tickRotation: isMobile ? -45 : 0,
            format: "%b '%y",
            tickValues: isMobile ? "every 6 months" : "every 3 months",
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: isMobile ? 8 : 12,
            tickRotation: 0,
            format: (value) => formatStockPrice({ price: value, currency }),
            tickValues: isMobile ? 4 : 5,
          }}
          pointSize={0}
          colors={["#22c55e"]}
          lineWidth={2.5}
          enableGridX={false}
          enableGridY={true}
          gridYValues={isMobile ? 4 : 5}
          enableArea={true}
          areaOpacity={0.4}
          areaBaselineValue={yMin}
          isInteractive={false}
          layers={chartLayers}
          theme={{
            background: "transparent",
            text: {
              fontSize: isMobile ? 11 : 12,
              fill: flatUIColors.textMuted,
            },
            axis: {
              domain: {
                line: {
                  stroke: "transparent",
                  strokeWidth: 0,
                },
              },
              ticks: {
                line: {
                  stroke: "transparent",
                  strokeWidth: 0,
                },
                text: {
                  fontSize: isMobile ? 11 : 12,
                  fill: flatUIColors.textPrimary,
                  fontWeight: 600,
                },
              },
            },
            grid: {
              line: {
                stroke: flatUIColors.border,
                strokeWidth: 1,
              },
            },
          }}
          defs={[
            {
              id: "priceGradient",
              type: "linearGradient",
              colors: [
                { offset: 0, color: "#22c55e", opacity: 0.5 },
                { offset: 50, color: "#22c55e", opacity: 0.25 },
                { offset: 100, color: "#22c55e", opacity: 0 },
              ],
            },
          ]}
          fill={[{ match: "*", id: "priceGradient" }]}
        />
      </Box>
    </Box>
  );
};

export default PriceTargetChart;
